import React, { ReactElement, ReactNode, SyntheticEvent } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Pill, IconWrapper } from './HBPill.styles';
import { PillType, PillSize } from './HBPill.types';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import type { CSSDimensionValue } from '../../tokens/types';

export interface Props {
    /** Semantic color theme mapped to status meanings (e.g., Draft, Success, Warning). */
    theme?: PillType;
    /** Text or node displayed inside the pill. */
    label: string | ReactNode;
    /** CSS class forwarded to the root element. */
    className?: string;
    /** Size variant. 'sm' for compact contexts; 'md' for more prominent display. Defaults to 'sm'. */
    size?: PillSize;
    /** Click handler. Providing this makes the pill interactive. */
    onClick?: (e: SyntheticEvent) => void;
    /** When true, preserves label casing; default is uppercase. */
    isLowerCase?: boolean;
    /** ReactElement rendered before the label, styled with the current theme color. */
    icon?: ReactElement;
    /** When true, the pill is non-interactive and visually muted. */
    isDisabled?: boolean;
    /** Replaces content with a skeleton placeholder while maintaining pill dimensions. */
    isLoading?: boolean;
    /** CSS margin applied to the root element. Accepts any valid CSS dimension value. */
    margin?: CSSDimensionValue;
}

const HBPill = ({
    testIdName,
    testId,
    label,
    className,
    theme = PillType.Draft,
    size = PillSize.Small,
    isLowerCase = false,
    icon,
    onClick,
    isDisabled,
    isLoading,
    margin
}: Props & MustIncludeOneOfBasicTestIdProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'pill',
        componentName: 'HBPill'
    });

    return (
        <Pill
            className={className}
            onClick={onClick}
            size={size}
            theme={theme}
            $isLowerCase={isLowerCase}
            isDisabled={isDisabled}
            $isLoading={isLoading}
            testId={finalTestId}
            $margin={margin}
        >
            {isLoading ? (
                <Skeleton />
            ) : (
                <>
                    {icon && <IconWrapper theme={theme}>{icon}</IconWrapper>}
                    {label}
                </>
            )}
        </Pill>
    );
};

export default HBPill;
